import type {CSSProperties} from 'react'
import {
  AvailabilityExceptionModel,
  AvailabilityExceptionType,
  AvailabilitySlotModel,
  AvailabilitySlotType,
} from '../core/_models'
// Type-only: types.ts imports AvailabilityLookup from this file, so a value
// import would close a cycle. `import type` is erased before that can happen.
import type {ConvertedHoursOfOperation} from '../types'

// Pure slot algebra for the availability grid. Everything here is total,
// immutable and store-free so the page can keep its whole editing model in
// local state — no emitter, no Dates, no timezones.
//
// The WEEKLY pattern is date-irrelevant: a slot is (ISO day_of_week, minutes
// from midnight). Only exceptions are stored — an uncovered stretch means
// available — so there is no 'available' type and erasing is a subtraction,
// not a paint.
//
// The DATED form (one real date) reuses all of it, because a date behaves
// exactly like a day once the column it belongs to is fixed. The one addition
// is the 'available' type: a date replaces its weekday outright, so it needs a
// way to say "yes, this one" rather than relying on absence — absence there
// means "this date has nothing of its own", which falls back to the weekday.

export const MINUTES_IN_DAY = 1440
export const STEP_MINUTES = 30
export const CELLS_PER_DAY = MINUTES_IN_DAY / STEP_MINUTES // 48

// ISO day-of-week, 0=Mon..6=Sun — the API's convention, deliberately
// independent of the company's configured week start (see helpers/templates.ts,
// which uses the same numbering for schedule templates).
export const ISO_DAYS = [0, 1, 2, 3, 4, 5, 6]

export const ISO_DAY_LABELS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

// What a drag paints. 'erase' is client-only: it subtracts rather than storing
// anything, because absence already means available.
export type PaintTool = AvailabilityExceptionType | 'erase'

/**
 * What the grid algebra below manipulates.
 *
 * The weekly and dated editors share every paint/merge/erase function, and
 * they differ only in what the day index MEANS — an ISO weekday for the
 * weekly grid, a column of the visible week for the dated one — which the
 * algebra never inspects. The one real difference is the type vocabulary, so
 * this widens `slot_type` and nothing else. `AvailabilitySlotModel` stays
 * narrow, which is what stops an 'available' slot being posted to the weekly
 * endpoint that has no such type.
 */
export type GridSlot = Omit<AvailabilitySlotModel, 'slot_type'> & {
  slot_type: AvailabilityExceptionType
}

export const SLOT_TYPE_LABELS: Record<AvailabilityExceptionType, string> = {
  unavailable: 'Unavailable',
  preferred: 'Preferred',
  not_preferred: 'Not Preferred',
  available: 'Available',
}

// Plain-language meaning of each state. The three type names are not
// self-explanatory in isolation — "Not Preferred" in particular has to say that
// the employee *can* still be scheduled — so the picker shows these underneath.
export const SLOT_TYPE_HINTS: Record<AvailabilityExceptionType, string> = {
  unavailable: 'Cannot work',
  preferred: 'Wants these hours',
  not_preferred: 'Can work, would rather not',
  available: 'Can work this date',
}

// Fills for the grid, in Bootstrap custom properties rather than MUI palette
// colours: the app switches themes by setting data-bs-theme on <html>
// (ThemeModeProvider), which MUI's useTheme() does not track — a palette colour
// renders light-mode inside a dark page. Each state carries a distinct PATTERN
// as well as a hue so they stay distinguishable without relying on colour.
export const SLOT_FILL_STYLE: Record<AvailabilityExceptionType, CSSProperties> = {
  // Dated only. Deliberately the plainest fill of the four: it states the
  // absence of a restriction, so it should not shout louder than the rules.
  available: {
    backgroundColor: 'rgba(var(--bs-primary-rgb), 0.28)',
  },
  unavailable: {
    backgroundColor: 'rgba(var(--bs-danger-rgb), 0.55)',
  },
  not_preferred: {
    backgroundColor: 'rgba(var(--bs-warning-rgb), 0.38)',
    backgroundImage:
      'repeating-linear-gradient(45deg, rgba(var(--bs-warning-rgb), 0.7) 0 4px, transparent 4px 8px)',
  },
  preferred: {
    // Heavier than the other two look like they need: --bs-success is a light
    // mint (#50cd89), so at the same alpha it all but vanishes against a white
    // light-mode background.
    backgroundColor: 'rgba(var(--bs-success-rgb), 0.5)',
    backgroundImage: 'radial-gradient(rgba(var(--bs-success-rgb), 0.95) 1.5px, transparent 1.5px)',
    backgroundSize: '6px 6px',
  },
}

/**
 * A board chip's fill: the same hue, on a surface of its own.
 *
 * SLOT_FILL_STYLE tints whatever is behind it, which is right for the paint
 * grid (the fill IS the cell) and wrong for a chip inside a cell: in dark mode
 * `rgba(--bs-danger, 0.55)` over a near-black cell comes out dark red, and the
 * theme-flipping `text-gray-900` label on it goes near-white — dark text on a
 * dark chip, unreadable. A chip is a label, so it carries its OWN white base
 * and the hue lands on that instead: the same pastel in both themes, with
 * black text that reads on all four states.
 *
 * `muted` is the standing weekly rule showing through a dated column. The veil
 * goes on TOP of the colour as a background layer, so it pales the fill
 * without touching the text — an `opacity` on the chip would fade the words
 * with it, which is the readability problem all over again.
 */
const flatLayer = (color: string) => `linear-gradient(${color}, ${color})`

export const slotChipStyle = (
  slotType: AvailabilityExceptionType,
  muted = false
): CSSProperties => {
  const fill = SLOT_FILL_STYLE[slotType]
  const images: string[] = []
  const sizes: string[] = []
  if (muted) {
    images.push(flatLayer('rgba(255, 255, 255, 0.62)'))
    sizes.push('auto')
  }
  if (fill.backgroundImage) {
    images.push(String(fill.backgroundImage))
    sizes.push(String(fill.backgroundSize ?? 'auto'))
  }
  if (fill.backgroundColor) {
    images.push(flatLayer(String(fill.backgroundColor)))
    sizes.push('auto')
  }
  return {
    backgroundColor: '#fff',
    backgroundImage: images.join(', '),
    backgroundSize: sizes.join(', '),
    // Fixed, not --bs-body-color: the chip's surface is white in both themes,
    // so its text has to be black in both themes too.
    color: '#000',
  }
}

export const cellIndexToMinutes = (index: number) => index * STEP_MINUTES

export const minutesToCellIndex = (minutes: number) => Math.floor(minutes / STEP_MINUTES)

export const minutesToLabel = (minutes: number, use12Hour = false) => {
  // 1440 is a real value here (end-of-day), and 24:00 reads better than 00:00
  // for the end of a range — so don't wrap it.
  const hour = Math.floor(minutes / 60)
  const minute = minutes % 60
  const mm = String(minute).padStart(2, '0')
  if (!use12Hour) return `${String(hour).padStart(2, '0')}:${mm}`
  const suffix = hour >= 12 && hour < 24 ? 'PM' : 'AM'
  const h12 = hour % 12 === 0 ? 12 : hour % 12
  return `${h12}:${mm} ${suffix}`
}

/** A stretch of one grid day the location is open, in minutes from its midnight. */
export interface OpenBand {
  startMinute: number
  endMinute: number
}

/**
 * Location opening hours → the grid's per-ISO-day open bands.
 *
 * Two conversions. ConvertedHoursOfOperation is keyed by the date-fns weekday
 * (0=Sun) and holds fractional hours, while the grid is ISO (0=Mon) and
 * minutes. And a close past midnight is filed under the day the shift STARTED,
 * as an end > 24h — that tail is open time on the next column, so it is carried
 * over rather than clamped away. A store open 10:00–03:00 is open 00:00–03:00
 * *and* 10:00–24:00 of every ISO day; clamping alone shaded midnight–03:00 as
 * closed, which for a late-night store is its busiest stretch.
 *
 * A day with no entry gets no band at all — the grid reads that as "hours
 * unknown" and leaves the column unshaded rather than declaring it shut.
 */
export const buildOpenBands = (
  hoursOfOperations: ConvertedHoursOfOperation | null | undefined
): Record<number, OpenBand[]> | undefined => {
  if (!hoursOfOperations) return undefined

  const bands: Record<number, OpenBand[]> = {}
  const addBand = (isoDay: number, startMinute: number, endMinute: number) => {
    if (endMinute <= startMinute) return
    if (!bands[isoDay]) bands[isoDay] = []
    bands[isoDay].push({startMinute, endMinute})
  }

  ISO_DAYS.forEach((isoDay) => {
    // ISO 0=Mon..6=Sun → date-fns 1=Mon..0=Sun.
    const hours = hoursOfOperations[(isoDay + 1) % 7]
    if (!hours) return
    const startMinute = Math.round(hours.start * 60)
    const endMinute = Math.round(hours.end * 60)

    addBand(isoDay, startMinute, Math.min(endMinute, MINUTES_IN_DAY))
    if (endMinute > MINUTES_IN_DAY) {
      const nextIsoDay = (isoDay + 1) % 7
      addBand(nextIsoDay, 0, Math.min(endMinute - MINUTES_IN_DAY, MINUTES_IN_DAY))
    }
  })

  return bands
}

export const formatSlotRange = (slot: GridSlot, use12Hour = false) =>
  `${minutesToLabel(slot.start_minute, use12Hour)}–${minutesToLabel(slot.end_minute, use12Hour)}`

export const isAllDay = (slot: GridSlot) =>
  slot.start_minute === 0 && slot.end_minute === MINUTES_IN_DAY

/**
 * Resolve a list of paint strokes into non-overlapping, sorted slots.
 *
 * Mirrors `normalize_slots` in fds-v2's
 * project/api_v2/labor_availability/service.py — the server re-normalizes
 * whatever it receives, so the two MUST agree or a save would silently
 * rewrite the grid the user is looking at. Keep them in sync.
 *
 * Slots are applied in order and a later stroke wins over the region it
 * covers, truncating or splitting what it lands on. Same-type neighbours that
 * touch or overlap are then merged; notes participate in identity, so slots
 * with different notes never merge (merging would drop one).
 */
export const normalizeSlots = (slots: GridSlot[]): GridSlot[] => {
  const byDay = new Map<number, GridSlot[]>()
  slots.forEach((slot) => {
    const day = byDay.get(slot.day_of_week)
    if (day) day.push(slot)
    else byDay.set(slot.day_of_week, [slot])
  })

  const out: GridSlot[] = []
  Array.from(byDay.keys())
    .sort((a, b) => a - b)
    .forEach((day) => {
      let resolved: GridSlot[] = []

      byDay.get(day)!.forEach((next) => {
        const {start_minute: start, end_minute: end} = next
        if (end <= start) return // degenerate stroke paints nothing

        const kept: GridSlot[] = []
        resolved.forEach((existing) => {
          if (existing.end_minute <= start || existing.start_minute >= end) {
            kept.push(existing) // disjoint
            return
          }
          // Overlap: keep whatever pokes out either side, drop the middle.
          if (existing.start_minute < start) kept.push({...existing, end_minute: start})
          if (existing.end_minute > end) kept.push({...existing, start_minute: end})
        })
        kept.push({
          day_of_week: day,
          slot_type: next.slot_type,
          start_minute: start,
          end_minute: end,
          note: next.note ?? null,
        })
        resolved = kept.sort((a, b) => a.start_minute - b.start_minute)
      })

      const merged: GridSlot[] = []
      resolved.forEach((slot) => {
        const prev = merged[merged.length - 1]
        if (
          prev &&
          prev.slot_type === slot.slot_type &&
          (prev.note ?? null) === (slot.note ?? null) &&
          prev.end_minute >= slot.start_minute
        ) {
          prev.end_minute = Math.max(prev.end_minute, slot.end_minute)
        } else {
          merged.push({...slot})
        }
      })
      out.push(...merged)
    })

  return out
}

/** Paint a range with a type. Later paint wins over whatever is beneath. */
export const paintRange = (
  slots: GridSlot[],
  dayOfWeek: number,
  startMinute: number,
  endMinute: number,
  slotType: AvailabilityExceptionType,
  note: string | null = null
): GridSlot[] =>
  normalizeSlots([
    ...slots,
    {
      day_of_week: dayOfWeek,
      slot_type: slotType,
      start_minute: startMinute,
      end_minute: endMinute,
      note,
    },
  ])

/**
 * Clear a range back to available. Subtraction rather than a paint: there is
 * no 'available' slot type, so erasing means removing coverage.
 */
export const eraseRange = (
  slots: GridSlot[],
  dayOfWeek: number,
  startMinute: number,
  endMinute: number
): GridSlot[] => {
  if (endMinute <= startMinute) return slots

  const out: GridSlot[] = []
  slots.forEach((slot) => {
    if (
      slot.day_of_week !== dayOfWeek ||
      slot.end_minute <= startMinute ||
      slot.start_minute >= endMinute
    ) {
      out.push(slot)
      return
    }
    if (slot.start_minute < startMinute) out.push({...slot, end_minute: startMinute})
    if (slot.end_minute > endMinute) out.push({...slot, start_minute: endMinute})
  })
  return normalizeSlots(out)
}

export const applyTool = (
  slots: GridSlot[],
  dayOfWeek: number,
  startMinute: number,
  endMinute: number,
  tool: PaintTool
): GridSlot[] =>
  tool === 'erase'
    ? eraseRange(slots, dayOfWeek, startMinute, endMinute)
    : paintRange(slots, dayOfWeek, startMinute, endMinute, tool)

export const slotsForDay = (slots: GridSlot[], dayOfWeek: number) =>
  slots
    .filter((slot) => slot.day_of_week === dayOfWeek)
    .sort((a, b) => a.start_minute - b.start_minute)

/** True when the whole day is blocked out — one full-span 'unavailable' slot. */
export const isDayFullyBlocked = (slots: GridSlot[], dayOfWeek: number) => {
  const day = slotsForDay(slots, dayOfWeek)
  return day.length === 1 && day[0].slot_type === 'unavailable' && isAllDay(day[0])
}

/**
 * The day-header toggle: block the entire day, or clear it if it already is.
 * Blocking replaces everything on that day, which is what "unavailable all
 * day" means — any preferred hours underneath are no longer true.
 */
export const toggleDayAllDay = (slots: GridSlot[], dayOfWeek: number) =>
  isDayFullyBlocked(slots, dayOfWeek)
    ? eraseRange(slots, dayOfWeek, 0, MINUTES_IN_DAY)
    : paintRange(slots, dayOfWeek, 0, MINUTES_IN_DAY, 'unavailable')

/** The slot covering a given minute, or undefined where the employee is available. */
export const slotAt = (slots: GridSlot[], dayOfWeek: number, minute: number) =>
  slots.find(
    (slot) =>
      slot.day_of_week === dayOfWeek && slot.start_minute <= minute && slot.end_minute > minute
  )

/**
 * Per-cell type lookup for rendering: day → 48 entries, null where available.
 * Built once per render so the 336 cells don't each scan the slot list.
 */
export const buildCellTypes = (
  slots: GridSlot[]
): Map<number, (AvailabilityExceptionType | null)[]> => {
  const map = new Map<number, (AvailabilityExceptionType | null)[]>()
  ISO_DAYS.forEach((day) => map.set(day, new Array(CELLS_PER_DAY).fill(null)))

  slots.forEach((slot) => {
    const cells = map.get(slot.day_of_week)
    if (!cells) return
    const from = minutesToCellIndex(slot.start_minute)
    // end is exclusive; ceil so a range ending mid-cell still colours it.
    const to = Math.min(CELLS_PER_DAY, Math.ceil(slot.end_minute / STEP_MINUTES))
    for (let i = from; i < to; i++) cells[i] = slot.slot_type
  })
  return map
}

/** True at the first cell of a run — where the grid draws the range's label. */
export const isRunStart = (cells: (AvailabilityExceptionType | null)[], index: number): boolean =>
  cells[index] !== null && (index === 0 || cells[index - 1] !== cells[index])

/** Stable comparison for the page's dirty check. */
export const slotsEqual = (a: GridSlot[], b: GridSlot[]) => {
  if (a.length !== b.length) return false
  const key = (slot: GridSlot) =>
    `${slot.day_of_week}|${slot.start_minute}|${slot.end_minute}|${slot.slot_type}|${
      slot.note ?? ''
    }`
  const left = a.map(key).sort()
  const right = b.map(key).sort()
  return left.every((value, index) => value === right[index])
}

// --- Schedule-board shading ------------------------------------------------
// The schedule grid renders ~1,200 Cells, each of which needs to know the
// selected employee's availability at its own half hour. Scanning a slot list
// per cell would be O(cells × slots), so the whole location is flattened once
// into a typed array per employee and each cell does a single index.

export const AVAILABILITY_CODE = {
  none: 0,
  unavailable: 1,
  not_preferred: 2,
  preferred: 3,
  // Dated only. On the board it shades nothing — "can work" is what an
  // unshaded cell already means — but it needs a code so a dated 'available'
  // can OVERWRITE the weekly shading it replaces rather than being skipped.
  available: 4,
} as const

export const CODE_TO_SLOT_TYPE: Record<number, AvailabilityExceptionType | null> = {
  0: null,
  1: 'unavailable',
  2: 'not_preferred',
  3: 'preferred',
}

/**
 * How the schedule board tints a cell. Much lighter than the availability
 * editor's own fills (SLOT_FILL_STYLE): there the fill IS the content, here it
 * sits under shifts, allowable-hours numbers and drag selection, so it has to
 * read as a whisper rather than a block of colour.
 *
 * **Never set `background-color` here.** The cell's fill comes from its own
 * theme class, and an inline background-color replaces it rather than tinting
 * it — which knocked a white hole through every shaded cell in dark mode, the
 * near-transparent tint showing the page behind the button instead of the
 * cell's dark fill. The wash is therefore a flat `linear-gradient` LAYER: it
 * paints over whatever background-color the theme gave the cell, in both
 * themes, and stacks under the pattern layer listed before it.
 *
 * The three states are told apart by PATTERN — hatch one way, hatch the other,
 * dots — because tint alone cannot carry them at this weight: `--bs-success`
 * is a light mint and `--bs-warning` a light yellow, both of which vanish on a
 * light cell at any alpha subtle enough to sit behind a shift. Thin
 * high-contrast marks stay legible while keeping the cell quiet, and survive
 * for colour-blind users — the same rule the editor grid follows.
 *
 * Bootstrap custom properties rather than MUI palette colours: the app themes
 * by setting data-bs-theme on <html>, which useTheme() does not track.
 */
const wash = (rgbVar: string, alpha = 0.06) =>
  `linear-gradient(rgba(var(${rgbVar}), ${alpha}), rgba(var(${rgbVar}), ${alpha}))`

export const BOARD_SHADING: Record<number, {style: CSSProperties; label: string}> = {
  // No entry for AVAILABILITY_CODE.available on purpose. It shades NOTHING —
  // an unshaded cell already reads as "can work" — and the lookup below
  // already answers {style: {}, label: null} for a code it does not carry.
  // Every entry that IS here must paint something, which is the invariant the
  // tests around this map protect.
  [AVAILABILITY_CODE.unavailable]: {
    style: {
      backgroundImage: [
        'repeating-linear-gradient(45deg, rgba(var(--bs-danger-rgb), 0.2) 0 2px, transparent 2px 8px)',
        wash('--bs-danger-rgb'),
      ].join(', '),
    },
    label: 'employee unavailable',
  },
  [AVAILABILITY_CODE.not_preferred]: {
    style: {
      backgroundImage: [
        'repeating-linear-gradient(-45deg, rgba(var(--bs-warning-rgb), 0.26) 0 2px, transparent 2px 8px)',
        wash('--bs-warning-rgb'),
      ].join(', '),
    },
    label: 'employee would rather not',
  },
  [AVAILABILITY_CODE.preferred]: {
    style: {
      backgroundImage: [
        'radial-gradient(rgba(var(--bs-success-rgb), 0.38) 1.1px, transparent 1.1px)',
        wash('--bs-success-rgb'),
      ].join(', '),
      // Per layer: the dot grid tiles, the wash covers the cell.
      backgroundSize: '8px 8px, auto',
    },
    label: 'employee prefers these hours',
  },
}

/** The board tint for a code, or nothing where the employee is simply available. */
export const boardShading = (code?: number): {style: CSSProperties; label: string | null} =>
  (code && BOARD_SHADING[code]) || {style: {}, label: null}

/** employee_id → 7×48 codes, indexed `day * CELLS_PER_DAY + cell`. */
export type AvailabilityLookup = Map<number, Uint8Array>

export const buildAvailabilityLookup = (
  items: {employee_id: number; slots?: GridSlot[] | null}[] = []
): AvailabilityLookup => {
  const lookup: AvailabilityLookup = new Map()
  items.forEach((item) => {
    if (!item.slots || !item.slots.length) return
    const codes = new Uint8Array(ISO_DAYS.length * CELLS_PER_DAY)
    item.slots.forEach((slot) => {
      if (slot.day_of_week < 0 || slot.day_of_week > 6) return
      const code = AVAILABILITY_CODE[slot.slot_type]
      const base = slot.day_of_week * CELLS_PER_DAY
      const from = minutesToCellIndex(slot.start_minute)
      const to = Math.min(CELLS_PER_DAY, Math.ceil(slot.end_minute / STEP_MINUTES))
      for (let i = from; i < to; i++) codes[base + i] = code
    })
    lookup.set(item.employee_id, codes)
  })
  return lookup
}

/**
 * The availability code for an employee at a moment. Takes a real Date because
 * that is what the schedule grid works in; the weekday is converted to the ISO
 * numbering availability is stored in (date-fns/JS is 0=Sun, ours is 0=Mon).
 */
export const availabilityCodeAt = (
  lookup: AvailabilityLookup | null | undefined,
  employeeId: unknown,
  when: Date
): number => {
  if (!lookup || typeof employeeId !== 'number') return AVAILABILITY_CODE.none
  const codes = lookup.get(employeeId)
  if (!codes) return AVAILABILITY_CODE.none
  const isoDay = (when.getDay() + 6) % 7
  const cell = minutesToCellIndex(when.getHours() * 60 + when.getMinutes())
  return codes[isoDay * CELLS_PER_DAY + cell] ?? AVAILABILITY_CODE.none
}

/** What a whole-day cell shows: one code, plus the hours behind it. */
export type DayAvailabilitySummary = {code: number; title: string | null}

const NO_DAY_AVAILABILITY: DayAvailabilitySummary = {code: AVAILABILITY_CODE.none, title: null}

/**
 * Collapse an employee's day into one code for the employee × day board, where
 * a cell is a whole day rather than a half hour.
 *
 * **Most restrictive wins**, which is what the code numbering already orders:
 * hours the employee cannot work outrank hours they would rather not, which
 * outrank hours they want. A day that is half blocked must not read as
 * "preferred" just because the other half is.
 *
 * The window narrows the summary to the day's opening hours — availability
 * painted while the store is shut says nothing about whether someone can be
 * scheduled. An empty or inverted window is ignored (no hours of operation
 * loaded yet) rather than blanking the shading.
 */
export const dayAvailabilitySummary = (
  lookup: AvailabilityLookup | null | undefined,
  employeeId: unknown,
  day: Date,
  fromMinute = 0,
  toMinute = MINUTES_IN_DAY
): DayAvailabilitySummary => {
  if (!lookup || typeof employeeId !== 'number') return NO_DAY_AVAILABILITY
  const codes = lookup.get(employeeId)
  if (!codes) return NO_DAY_AVAILABILITY

  const windowed = toMinute > fromMinute
  const from = windowed ? Math.max(0, minutesToCellIndex(fromMinute)) : 0
  const to = windowed ? Math.min(CELLS_PER_DAY, Math.ceil(toMinute / STEP_MINUTES)) : CELLS_PER_DAY

  const base = ((day.getDay() + 6) % 7) * CELLS_PER_DAY
  let winner: number = AVAILABILITY_CODE.none
  for (let i = from; i < to; i++) {
    const code = codes[base + i]
    if (code === AVAILABILITY_CODE.none) continue
    if (winner === AVAILABILITY_CODE.none || code < winner) winner = code
  }
  if (winner === AVAILABILITY_CODE.none) return NO_DAY_AVAILABILITY

  // The runs of the winning code, so the tooltip names the hours the shading
  // is about instead of just the day. Walks one cell past the end to close a
  // run that reaches it. Runs are clipped to the window, which is the point —
  // hours the store is shut are not hours anyone can be scheduled for.
  const runs: string[] = []
  let runStart = -1
  for (let i = from; i <= to; i++) {
    const isWinner = i < to && codes[base + i] === winner
    if (isWinner && runStart < 0) runStart = i
    if (!isWinner && runStart >= 0) {
      runs.push(
        `${minutesToLabel(cellIndexToMinutes(runStart), true)}–${minutesToLabel(
          cellIndexToMinutes(i),
          true
        )}`
      )
      runStart = -1
    }
  }

  // "All day" means the whole DAY, tested against all 48 cells rather than
  // against the window: a block that merely fills a short opening-hours window
  // (11:00–15:00, say) is not an all-day block, and calling it one contradicts
  // the availability page the user painted it on.
  let allDay = true
  for (let i = 0; i < CELLS_PER_DAY; i++) {
    if (codes[base + i] !== winner) {
      allDay = false
      break
    }
  }

  const label = SLOT_TYPE_LABELS[CODE_TO_SLOT_TYPE[winner]!]
  return {code: winner, title: allDay ? `${label} all day` : `${label} ${runs.join(', ')}`}
}

/** Server payload → editing model (drops server-owned fields). */
export const apiToSlots = (slots: AvailabilitySlotModel[] = []): GridSlot[] =>
  normalizeSlots(
    slots.map((slot) => ({
      day_of_week: slot.day_of_week,
      slot_type: slot.slot_type,
      start_minute: slot.start_minute,
      end_minute: slot.end_minute,
      note: slot.note ?? null,
    }))
  )

// --- Dated mode ------------------------------------------------------------
// The dated editor shows ONE week at a time and reuses the 7-column grid, so a
// column index is all the algebra above ever sees. These convert between that
// index and the real date it stands for.

/**
 * A date as the API's `date_key`, in LOCAL time.
 *
 * Not `toISOString().slice(0, 10)`: that converts to UTC first, so anywhere
 * west of Greenwich an evening date lands on the following day and the entry
 * is saved against the wrong one.
 */
export const toDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`

/** A `date_key` back to a local Date at midnight, free of the same UTC shift. */
export const fromDateKey = (key: string) => {
  const [year, month, day] = key.split('-').map(Number)
  return new Date(year, (month || 1) - 1, day || 1)
}

/** ISO weekday (0=Mon..6=Sun) from a Date, whose getDay() is 0=Sun..6=Sat. */
export const isoDayOf = (date: Date) => (date.getDay() + 6) % 7

/** The 7 dates of a week, in column order. */
export const weekDatesFrom = (weekStart: Date) =>
  ISO_DAYS.map((offset) => {
    const date = new Date(weekStart)
    date.setDate(weekStart.getDate() + offset)
    return date
  })

/**
 * Dated slots → grid slots, keyed by COLUMN rather than weekday.
 *
 * A date outside the visible week is dropped rather than folded onto a column
 * it does not belong to.
 */
export const exceptionsToGrid = (
  exceptions: AvailabilityExceptionModel[],
  weekDates: Date[]
): GridSlot[] => {
  const columnOf = new Map(weekDates.map((date, index) => [toDateKey(date), index]))
  const out: GridSlot[] = []
  exceptions.forEach((exception) => {
    const column = columnOf.get(exception.date_key)
    if (column === undefined) return
    out.push({
      uuid: exception.uuid,
      day_of_week: column,
      slot_type: exception.slot_type,
      start_minute: exception.start_minute,
      end_minute: exception.end_minute,
      note: exception.note ?? null,
    })
  })
  return normalizeSlots(out)
}

/** Grid slots → dated slots, putting each column back on its date. */
export const gridToExceptions = (
  slots: GridSlot[],
  weekDates: Date[]
): AvailabilityExceptionModel[] =>
  slots
    .filter((slot) => weekDates[slot.day_of_week] !== undefined)
    .map((slot) => ({
      date_key: toDateKey(weekDates[slot.day_of_week]),
      slot_type: slot.slot_type,
      start_minute: slot.start_minute,
      end_minute: slot.end_minute,
      note: slot.note ?? null,
    }))

/**
 * The weekly pattern laid out under the visible week, as the faded underlay.
 *
 * Only the hours a dated entry actually covers are cut out of it. A dated row
 * overrides its weekday minute by minute rather than replacing the day, so a
 * note about Monday afternoon must leave the standing Monday-morning rule
 * visibly in force — blanking the column would show the week as if a rule
 * nobody touched had been deleted.
 */
export const weeklyUnderlay = (
  weekly: GridSlot[],
  weekDates: Date[],
  dated: GridSlot[]
): GridSlot[] => {
  let out: GridSlot[] = []
  weekDates.forEach((date, column) => {
    const isoDay = isoDayOf(date)
    weekly
      .filter((slot) => slot.day_of_week === isoDay)
      .forEach((slot) => out.push({...slot, uuid: undefined, day_of_week: column}))
  })
  // Whatever the date says — including 'available', which says nothing and so
  // simply cancels — wins for the minutes it covers.
  dated.forEach((slot) => {
    out = eraseRange(out, slot.day_of_week, slot.start_minute, slot.end_minute)
  })
  return out
}

/**
 * Overlay a week's dated exceptions onto a board lookup, in place.
 *
 * The board draws exactly one week, and the lookup is indexed by ISO weekday,
 * so each date in view maps onto one column of it. Each dated row is painted
 * over just the cells it covers — the same layering the backend applies when
 * it judges a shift — so the weekly rule stands everywhere the date is silent.
 *
 * `available` paints its own code, which no shading is registered for: those
 * cells come out clear, which is exactly what cancelling a weekly rule for
 * those hours should look like.
 */
export const applyExceptionsToLookup = (
  lookup: AvailabilityLookup,
  exceptions: AvailabilityExceptionModel[],
  weekDates: Date[]
): AvailabilityLookup => {
  if (!exceptions.length) return lookup

  const isoDayFor = new Map(weekDates.map((date) => [toDateKey(date), isoDayOf(date)]))

  exceptions.forEach((exception) => {
    const employeeId = exception.employee_id
    const isoDay = isoDayFor.get(exception.date_key)
    if (employeeId == null || isoDay === undefined) return

    let codes = lookup.get(employeeId)
    if (!codes) {
      // An employee with no weekly pattern at all still needs a row once a
      // date speaks for them.
      codes = new Uint8Array(ISO_DAYS.length * CELLS_PER_DAY)
      lookup.set(employeeId, codes)
    }

    const base = isoDay * CELLS_PER_DAY
    const code = AVAILABILITY_CODE[exception.slot_type]
    const from = minutesToCellIndex(exception.start_minute)
    const to = Math.min(CELLS_PER_DAY, Math.ceil(exception.end_minute / STEP_MINUTES))
    for (let i = from; i < to; i++) codes[base + i] = code
  })

  return lookup
}

// --- The employee × day board ----------------------------------------------
// The roster-wide layout: one row per employee, one column per day, a whole
// day per cell. It reads exactly the same slots as the paint grid, but nothing
// is painted here — a cell is a LIST of entries, edited in a modal — so what
// it needs from this file is grouping per employee, a way to write one day
// back, and the layering that decides what a dated column still shows of the
// standing weekly rule.

/** One column of the board. */
export interface BoardColumn {
  /** The ISO weekday (0=Mon) the column stands for. */
  isoDay: number
  /** The real date, in dated mode only — a weekly column stands for every such weekday. */
  date?: Date
}

/** One entry drawn inside a cell. */
export interface BoardEntry {
  slot: GridSlot
  /**
   * A standing weekly rule the date has not overridden. Drawn faded and not
   * editable from a dated cell: painting there writes a date, and the rule
   * itself belongs to the weekly column.
   */
  inherited: boolean
}

/**
 * The ISO weekdays in column order for a company whose week starts on
 * `weekStartsOn` (date-fns numbering, 0=Sun).
 *
 * The paint grid always runs Monday-first because its columns ARE the ISO
 * days. This board is read against the schedule next to it, so its columns
 * follow the company's week start instead — a store whose week starts on
 * Sunday reads its availability in the order it reads its shifts.
 */
export const columnIsoDays = (weekStartsOn = 1) => {
  const first = ((weekStartsOn % 7) + 6) % 7
  return ISO_DAYS.map((offset) => (first + offset) % 7)
}

/** The board's columns: seven real dates in dated mode, seven weekdays otherwise. */
export const boardColumns = (weekStartsOn = 1, weekDates?: Date[]): BoardColumn[] =>
  weekDates?.length === ISO_DAYS.length
    ? weekDates.map((date) => ({isoDay: isoDayOf(date), date}))
    : columnIsoDays(weekStartsOn).map((isoDay) => ({isoDay}))

/** The location-wide weekly payload → employee_id → their pattern. */
export const groupWeeklyByEmployee = (
  items: {employee_id: number; slots?: AvailabilitySlotModel[] | null}[] = []
): Map<number, GridSlot[]> => {
  const out = new Map<number, GridSlot[]>()
  items.forEach((item) => {
    if (!item.slots || !item.slots.length) return
    out.set(item.employee_id, apiToSlots(item.slots))
  })
  return out
}

/**
 * The location-wide dated payload → employee_id → COLUMN-keyed slots.
 *
 * Keyed by column rather than by date for the same reason the dated editor is:
 * once the visible week is fixed, a column index is all the slot algebra ever
 * needs. Dates outside the week are dropped by `exceptionsToGrid`.
 */
export const groupExceptionsByEmployee = (
  exceptions: AvailabilityExceptionModel[] = [],
  weekDates: Date[]
): Map<number, GridSlot[]> => {
  const byEmployee = new Map<number, AvailabilityExceptionModel[]>()
  exceptions.forEach((exception) => {
    const employeeId = exception.employee_id
    if (employeeId == null) return
    const list = byEmployee.get(employeeId)
    if (list) list.push(exception)
    else byEmployee.set(employeeId, [exception])
  })

  const out = new Map<number, GridSlot[]>()
  byEmployee.forEach((list, employeeId) => out.set(employeeId, exceptionsToGrid(list, weekDates)))
  return out
}

/**
 * One board row: what each column shows, in column order.
 *
 * Weekly mode is the pattern alone. Dated mode layers the two the way the
 * backend does when it judges a shift — the date's own entries at full
 * strength, and the weekly rule still in force for the hours the date leaves
 * alone, faded. That subtraction is `weeklyUnderlay`'s, so a dated entry over
 * Monday afternoon leaves the standing Monday-morning rule visible rather than
 * blanking the column, which reads as data loss.
 *
 * Both lists are sorted together by start time: a reader wants the day in
 * order, not the dated entries first.
 */
export const buildBoardRow = (
  columns: BoardColumn[],
  weekly: GridSlot[] = [],
  dated?: GridSlot[]
): BoardEntry[][] => {
  const weekDates = columns.map((column) => column.date)
  const datedMode = !!dated && weekDates.every((date): date is Date => !!date)
  if (!datedMode) {
    return columns.map((column) =>
      slotsForDay(weekly, column.isoDay).map((slot) => ({slot, inherited: false}))
    )
  }

  const underlay = weeklyUnderlay(weekly, weekDates as Date[], dated!)
  return columns.map((_column, index) =>
    [
      ...slotsForDay(underlay, index).map((slot) => ({slot, inherited: true})),
      ...slotsForDay(dated!, index).map((slot) => ({slot, inherited: false})),
    ].sort((a, b) => a.slot.start_minute - b.slot.start_minute)
  )
}

/**
 * Replace one day's entries inside an employee's whole list.
 *
 * The weekly endpoint is a full replace, so saving one cell means sending the
 * other six days back untouched — this is what builds that payload.
 */
export const replaceDaySlots = (
  slots: GridSlot[],
  dayOfWeek: number,
  daySlots: GridSlot[]
): GridSlot[] =>
  normalizeSlots([
    ...slots.filter((slot) => slot.day_of_week !== dayOfWeek),
    ...daySlots.map((slot) => ({...slot, day_of_week: dayOfWeek})),
  ])

/**
 * Grid slots → the weekly endpoint's payload, dropping the dated-only type.
 *
 * 'available' states the absence of a restriction, which the weekly table
 * expresses by having no slot at all — there is no column for it, and posting
 * one is a 422. The editors only offer it in dated mode, and this is the
 * runtime half of that promise.
 */
export const toWeeklySlots = (slots: GridSlot[]): AvailabilitySlotModel[] =>
  slots
    .filter((slot) => slot.slot_type !== 'available')
    .map((slot) => ({
      day_of_week: slot.day_of_week,
      slot_type: slot.slot_type as AvailabilitySlotType,
      start_minute: slot.start_minute,
      end_minute: slot.end_minute,
      note: slot.note ?? null,
    }))
